import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { useInspectionListRefreshStore } from '../stores/inspectionListRefresh';
import { extractApiError } from '../lib/api';
import {
  completeMyInspection,
  cancelMyCompletion,
  requestCompletion,
  excludeContributor,
  CompleteMyInspectionInput,
  ExcludeContributorInput,
} from '../lib/inspections';
interface ContributorCompletion {
  id: string;
  userId?: string;
  userName?: string;
  role: string;
  collaboratorId?: string;
  completionStatus?: 'IN_PROGRESS' | 'COMPLETED';
  completedAt?: string;
  signerName?: string;
  signatureData?: string;
  excludedAt?: string;
  /** 사진 업로드 기여 (실시간 계산) */
  hasPhotoContribution?: boolean;
  /** 개선대책 선택 기여 (실시간 계산) */
  hasMeasureContribution?: boolean;
}

interface UseInspectionCompletionOptions {
  inspectionId: string | undefined;
  companyId: string | undefined;
  contributors: ContributorCompletion[];
  hasReport: boolean;
  /** 개선대책이 1건 이상 선택된 사진이 있는지 */
  hasAnyMeasures: boolean;
}

export function useInspectionCompletion({
  inspectionId,
  companyId,
  contributors,
  hasReport,
  hasAnyMeasures,
}: UseInspectionCompletionOptions) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const currentUserId = user?.id || '';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 완료/완료취소는 같은 현장 다른 점검의 시작 가능 여부까지 바꾸므로 현장 단위로 갱신
  const invalidateSiteInspectionScope = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
    queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
    // 점검 목록 화면은 react-query가 아닌 수동 state + refreshTrigger 구독
    useInspectionListRefreshStore.getState().triggerRefresh();
  }, [queryClient]);

  // 활성 참여자 (제외되지 않은)
  const activeContributors = useMemo(
    () => contributors.filter((c) => !c.excludedAt),
    [contributors]
  );

  // 내 참여자 정보 (userId가 있으면 userId로, 없으면 id로 매칭)
  const myCollaborator = useMemo(
    () => activeContributors.find((c) => (c.userId || c.id) === currentUserId),
    [activeContributors, currentUserId]
  );

  // 완료 상태 계산
  const myCompletionStatus = myCollaborator?.completionStatus || 'IN_PROGRESS';
  const isMyCompleted = myCompletionStatus === 'COMPLETED';

  // 실제 점검 기여자 (사진 업로드자 OR 개선대책 선택자)
  // 결정 기록: docs/decisions/2026-04-19-contributor-definition-expansion.md
  const workContributors = useMemo(
    () =>
      activeContributors.filter(
        (c) => c.hasPhotoContribution || c.hasMeasureContribution
      ),
    [activeContributors]
  );

  const completedCount = workContributors.filter(
    (c) => c.completionStatus === 'COMPLETED'
  ).length;
  const totalCount = workContributors.length;

  const allCompleted = totalCount > 0 && completedCount === totalCount;

  const pendingContributors = workContributors.filter(
    (c) => c.completionStatus !== 'COMPLETED'
  );

  // 비참여자: 점검완료 관련 버튼 전면 비노출
  const isParticipant = !!myCollaborator;

  // 본인이 기여자인지 (사진 업로드 OR measure 선택)
  const isContributor =
    (myCollaborator?.hasPhotoContribution ?? false) ||
    (myCollaborator?.hasMeasureContribution ?? false);

  // 버튼 상태 결정 — 기여자(사진/measure 중 하나) 기반
  const canSign = isContributor && !isMyCompleted && hasAnyMeasures;
  const canCancel = isContributor && isMyCompleted && !hasReport;
  const canRequest = isContributor && isMyCompleted && pendingContributors.length > 0;
  const canGenerateReport = allCompleted;
  const signDisabledReason = !hasAnyMeasures
    ? '개선대책을 1건 이상 선택해주세요'
    : !isParticipant
    ? '점검 참여자만 서명할 수 있습니다'
    : !isContributor
    ? '사진 업로드 또는 개선대책 선택 중 하나가 필요합니다'
    : '';

  // 점검완료 서명
  const handleCompleteMyInspection = useCallback(
    async (data: CompleteMyInspectionInput) => {
      if (!inspectionId || !companyId) return;
      setIsSubmitting(true);
      setError('');
      try {
        await completeMyInspection(inspectionId, companyId, data);
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
        // 같은 현장 다른 점검의 시작 가능 여부(canStart/blockingInspectionId)가 함께 바뀐다.
        // 웹소켓 이벤트가 유실되면 시작 버튼이 stale 비활성으로 남으므로 목록/일정도 갱신.
        invalidateSiteInspectionScope();
      } catch (err) {
        const apiError = extractApiError(err);
        setError(apiError.message);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [inspectionId, companyId, queryClient, invalidateSiteInspectionScope]
  );

  // 점검완료 취소
  const handleCancelMyCompletion = useCallback(async () => {
    if (!inspectionId || !companyId) return;
    setIsSubmitting(true);
    setError('');
    try {
      await cancelMyCompletion(inspectionId, companyId);
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
      // 완료 취소(COMPLETED → IN_PROGRESS)도 다른 점검의 시작 가능 여부를 되돌린다.
      invalidateSiteInspectionScope();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [inspectionId, companyId, queryClient, invalidateSiteInspectionScope]);

  // 점검완료 요청
  const handleRequestCompletion = useCallback(async () => {
    if (!inspectionId || !companyId) return;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await requestCompletion(inspectionId, companyId);
      return result.data;
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [inspectionId, companyId]);

  // 참여자 제외
  const handleExcludeContributor = useCallback(
    async (data: ExcludeContributorInput) => {
      if (!inspectionId || !companyId) return;
      setIsSubmitting(true);
      setError('');
      try {
        await excludeContributor(inspectionId, companyId, data);
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
        queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
      } catch (err) {
        const apiError = extractApiError(err);
        setError(apiError.message);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [inspectionId, companyId, queryClient]
  );

  return {
    // 점검완료 서명 기능 (항상 활성)
    completionSignatureEnabled: true,
    // 상태
    isSubmitting,
    error,
    // 완료 현황
    myCompletionStatus,
    isMyCompleted,
    completedCount,
    totalCount,
    allCompleted,
    pendingContributors,
    activeContributors,
    // 버튼 상태
    canSign,
    canCancel,
    canRequest,
    canGenerateReport,
    signDisabledReason,
    // 핸들러
    handleCompleteMyInspection,
    handleCancelMyCompletion,
    handleRequestCompletion,
    handleExcludeContributor,
  };
}
